// Models registry client — first-class custom ML per project.
//
// A model is NOT an agent: it is deterministic and versioned, so this surface is
// built around versions, metrics, LINEAGE (which Dolt commit trained it) and
// promotion — none of which the agent surface has. See docs/ml-models-surface.md.
import { fetchData } from '../fetchapi/fetchapi';

const json = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// { models: [{ id, name, display_name, task, target_column, visibility,
//              owner_email, shared_with_me, version_count, active_version,
//              active_metrics, active_dolt_commit, active_trained_at }] }
export async function listModels({ projectSlug } = {}) {
  return fetchData('models', projectSlug
    ? { headers: { 'X-Cortex-Project': projectSlug, 'X-Cortex-Branch': 'main' } }
    : undefined);
}

// { model, versions: [...], shares: [...], can_write }
export async function getModel(id) {
  return fetchData(`models/${id}`);
}

// { name, display_name?, description?, icon?, task?, target_column?, visibility? }
export async function createModel(body) {
  return fetchData('models', json('POST', body));
}

export async function updateModelVisibility(id, visibility) {
  return fetchData(`models/${id}`, json('PATCH', { visibility }));
}

// Description is part of the manifest, so the backend republishes to the fork.
export async function updateModelDescription(id, description) {
  return fetchData(`models/${id}`, json('PATCH', { description }));
}

// The LIVE manifest read off the project's Flow fork (cortex.model.<name>.manifest)
// — the authority the engine and both assistants see, not the ops mirror.
export async function getModelManifest(id) {
  return fetchData(`models/${id}/manifest`);
}

export async function removeModel(id) {
  return fetchData(`models/${id}`, { method: 'DELETE' });
}

// Promotion is one call; rolling back is the same call with an older version id.
export async function promoteVersion(id, versionId) {
  return fetchData(`models/${id}/promote`, json('POST', { version_id: versionId }));
}

// can_score separates auditing a model (metrics / lineage / importances) from
// running it — a reviewer often needs the former without the latter.
export async function shareModel(id, userId, canScore = false) {
  return fetchData(`models/${id}/share`, json('POST', { user_id: userId, can_score: canScore }));
}

export async function unshareModel(id, userId) {
  return fetchData(`models/${id}/share/${userId}`, { method: 'DELETE' });
}

// ── engine ────────────────────────────────────────────────────────────────
// Thin proxy to the `ml` engine on Flow core. Actions are allowlisted server
// side (train | predict | score_batch); the client can never name an endpoint.
export async function trainModel(params) {
  return fetchData('ml/engine', json('POST', { action: 'train', params }));
}

export async function predictWith(params) {
  return fetchData('ml/engine', json('POST', { action: 'predict', params }));
}

export async function scoreBatch(params) {
  return fetchData('ml/engine', json('POST', { action: 'score_batch', params }));
}

// The engine's own catalog: tasks, algorithms (key, label, family, tasks,
// params, steps) and the series contract. The page renders this rather than a
// hardcoded list, so a new algorithm in the `ml` service needs no UI change.
export async function mlCatalog() {
  return fetchData('ml/engine', json('POST', { action: 'catalog', params: {} }));
}
